import { uuid } from "config/app-constants";
import React, {ReactElement, useEffect, useRef, useState} from "react";
import Button from "../button/Button";
import Translate from "../Translate";
import { TabPropsType } from './Tab';
import './Tabs.scss';
import lodash from 'lodash';
import { isHidden } from "utilities/app-utilities";

// #region types
type TabItemType = {
    name: string,
    title: ReactElement,
    reloadOnFocus: boolean,
    content: ReactElement,
    loaded: boolean,
    hide?: any | (() => any)
}

type TabsPropsType = {
    children: ReactElement<TabPropsType> | Array<ReactElement<TabPropsType>>,
    activeTab?: number | string,
    onTabChange?: (tab: string) => void
}
// #endregion types

const Tabs = (props: TabsPropsType) => {
    const [tabs, setTabs] = useState<TabItemType[]>([]);
    const [activeTab, setActiveTab] = useState<TabItemType>();
    const arrowFirst = useRef<HTMLDivElement>(null);
    const arrowLast = useRef<HTMLDivElement>(null);
    const navElement = useRef<HTMLUListElement>(null);

    useEffect(() => {
        const ts = getTabs(props.children, props.activeTab);
        setTabs(ts);
        setActiveTab(ts.find(c => c.loaded) || ts[0]);
    }, [props.activeTab])


    useEffect(() => {
        setEffect(navElement?.current);
    }, [navElement.current])

    function onChangeTab(_: React.MouseEvent | undefined, tab: TabItemType) {
        if (activeTab?.reloadOnFocus) {
            const index = getTabIndex(tabs, activeTab.name);
            tabs[index].loaded = false;
        }

        const nextTabIndex = getTabIndex(tabs, tab.name);
        tabs[nextTabIndex].loaded = true;

        if(props.onTabChange) {
            props.onTabChange(tab.name);
        }
        setActiveTab({ ...tab });
        setTabs([...tabs]);
    }

    function onScroll(event?: React.UIEvent<HTMLElement>) {
        setEffect(event?.currentTarget);
    }

    function setEffect(element: HTMLElement | undefined | null) {
        if(!element) return;

        if(element.offsetWidth === element.scrollWidth) {
            arrowFirst.current?.classList.add('hidden');
            arrowLast.current?.classList.add('hidden');
        } else {
            const leftFunc = element.scrollLeft === 0 ? 'add' : 'remove';
            arrowFirst.current?.classList[leftFunc]('hidden');
            if(leftFunc === 'remove') {
                let opticality = "1";
                let fistelementLength = element.querySelectorAll('li')[0].offsetWidth;
                if(element.scrollLeft < fistelementLength) {
                    opticality = calculatePercentage(element.scrollLeft, fistelementLength);
                }

                arrowFirst.current!.style.opacity = opticality;
            }

            const right = element.scrollLeft + element.offsetWidth >= element.scrollWidth
                ? 'add' : 'remove';
            arrowLast.current?.classList[right]('hidden');
            if(right === 'remove') {
                let opticality = "1";
                let lis = element.querySelectorAll('li');
                const fistelementLength = lis[lis.length - 1].offsetWidth;
                const scrollLeft = element.scrollWidth - (element.scrollLeft + element.offsetWidth)
                if(scrollLeft < fistelementLength) {
                    opticality = calculatePercentage(scrollLeft, fistelementLength);
                }

                arrowLast.current!.style.opacity = opticality;
            }
        }
    }

    function calculatePercentage(part: number, whole: number) : string {
        let percent = (part / whole) * 100;
        percent = Math.ceil(percent);
        if(percent === 100) return "1";
        return "0." + (percent < 10 ? `0${percent}` : `${percent}`)
    }
    function scrollTo(position: 'start' | 'end') {
        let scrollTo = position === 'start' ? 0 : navElement.current?.scrollWidth;
        navElement.current?.scrollTo({
            left: scrollTo,
            behavior: "smooth"
        });
    }

    return (
        <>
            <div className="app-tabs">
                <header>
                    <div className="arrow first hidden" ref={arrowFirst}>
                        <div className="button-container">
                            <Button title="left" theme="link" onClick={() => scrollTo('start')}>
                                <>{"<"}</>
                            </Button>
                        </div>
                        <div className="shadow-container">
                        </div>
                    </div>
                    <ul ref={navElement} onScroll={onScroll}>
                        {tabs.filter(t => !isHidden(t.hide)).map((t) => {
                            const className = `${t.name === activeTab?.name ? 'active' : ''}`.trim();
                            return <li key={t.name} className={className}>
                                <Button theme="link" onClick={e => onChangeTab(e, t)} title="">{t.title}</Button>
                            </li>
                        })}
                    </ul>
                    <div className="arrow last" ref={arrowLast}>
                        <div className="shadow-container">
                        </div>
                        <div className="button-container">
                            <Button title="right" theme="link" onClick={() => scrollTo('end')}>
                                <>{">"}</>
                            </Button>
                        </div>
                    </div>
                </header>
                <>
                    {tabs.filter(t => t.loaded && !isHidden(t.hide)).map(t => {
                        const className = `app-tabs_tab-content ${t.name !== activeTab?.name ? 'hidden' : ''}`.trim();
                        return <section key={`body-tab-${t.name}`} className={className}>
                            {Array.isArray(props.children) ? props.children.find(c => c.props.name === t.name) : props.children}
                        </section>
                    })}
                </>
            </div>
        </>
    );
}

// #region private

function getTabs(children: ReactElement<TabPropsType> | Array<ReactElement<TabPropsType>>, activeTabIndex?: number | string) {
    let tabs: TabItemType[] = [];
    const childrenTabs = Array.isArray(children) ? children : [children]
    activeTabIndex = activeTabIndex || 0;

    childrenTabs.forEach((c, i) => {
        const tab = getTab(c, i, activeTabIndex!);
        if (tab) {
            tabs.push(tab);
        }
    });

    return tabs;
}

function getTab(tab: any, index: number, activeTabIndex: string | number): TabItemType | undefined {
    // FIXME: find a way to check if tab is a Tab component!
    // if (tab.type.name === 'Tab') {
        return {
            title: typeof tab.props.title == 'string' ? <Translate i18nKey={tab.props.title} /> : tab.props.title,
            name: tab.props.name || uuid(),
            reloadOnFocus: tab.props.reloadOnFocus || false,
            content: tab,
            loaded: tab.props.name === activeTabIndex || index === activeTabIndex,
            hide: tab.props.hide
        };

    // return undefined;
}

function getTabIndex(tabs: TabItemType[], index: number | string): number {
    if (typeof index == 'number') return index;
    return lodash.findIndex(tabs, c => c.name === index);
}
// #endregion private

export default Tabs;
